'use client';

import {
  createContext,
  createElement,
  useContext,
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
  type Ref,
} from 'react';

/**
 * Navigation, decided by whoever is rendering.
 *
 * These components have been shared by applications that navigate in different
 * ways: the Next Worker wanted `next/link`, the React app wants TanStack
 * Router's `Link` so a click does not reload the page, and the Astro storefront
 * wanted a plain anchor because a full navigation is the correct and fastest
 * thing for a content page.
 *
 * One of those is left and the indirection stays, because the *launcher* is the
 * next one: it serves this same bundle from a custom protocol, and a link there
 * has to be a router navigation rather than a page load a webview cannot do.
 *
 * So the component library does not choose. It renders whatever the application
 * provided, defaulting to an anchor — which is the one that always works, and
 * the one a crawler and a middle-click both understand.
 */

/**
 * Every optional prop admits `undefined` explicitly.
 *
 * The workspace runs `exactOptionalPropertyTypes`, under which `className?:
 * string` and `className: string | undefined` are different types — and every
 * caller here forwards a value that may be undefined rather than omitting the
 * prop. Spelling it out is what lets them.
 */
export interface LinkProps {
  href: string;
  className?: string | undefined;
  children?: ReactNode | undefined;
  title?: string | undefined;
  style?: CSSProperties | undefined;
  rel?: string | undefined;
  target?: string | undefined;
  'aria-label'?: string | undefined;
  'aria-current'?: 'page' | undefined;
  /**
   * Marks a card that may turn under the pointer.
   *
   * A data attribute rather than a class, because the class names in this
   * package are hashed by CSS modules and nothing outside it can name one. It
   * used to be read by a script in the storefront's layout; `useTilt` in
   * `components/store.tsx` is what moves the card now, and this is what the
   * stylesheet and the brand suite key off.
   */
  'data-tilt'?: '' | undefined;
  /**
   * The element itself, and the two events that turn it.
   *
   * **Added because the capsule's tilt stopped being a page's business.** The
   * storefront scanned the document for `[data-tilt]` and attached listeners
   * from outside; there is no page-level script any more, so the card wires
   * itself — and a card whose outermost element *is* the link has nowhere else
   * to put a handler.
   *
   * Listed one at a time rather than widening this to
   * `AnchorHTMLAttributes`, because the interface being closed is the point:
   * every renderer has to forward what it accepts, and a prop nobody forwards
   * is a prop that silently does nothing. `RouterLinkAdapter` in the app is the
   * renderer that would have dropped these.
   */
  ref?: Ref<HTMLElement> | undefined;
  onPointerMove?: ((event: PointerEvent) => void) | undefined;
  onPointerLeave?: (() => void) | undefined;
  onPointerEnter?: (() => void) | undefined;
  onPointerDown?: (() => void) | undefined;
}

export type LinkComponent = (props: LinkProps) => ReactNode;

const PlainAnchor: LinkComponent = ({ href, children, ...rest }) =>
  createElement('a', { href, ...rest }, children);

const LinkContext = createContext<LinkComponent>(PlainAnchor);

export function LinkProvider({
  component,
  children,
}: {
  component: LinkComponent;
  children: ReactNode;
}): ReactNode {
  return createElement(LinkContext.Provider, { value: component }, children);
}

/**
 * The link component for the current application.
 *
 * An absolute href always renders an anchor regardless of what was provided:
 * a client-side router cannot navigate across an origin, and GOODBUILD's three
 * sites are three origins. That rule lived in `Cta` and in the store card
 * before; it belongs here, once, where every link passes through.
 */
export function Link(props: LinkProps): ReactNode {
  const Provided = useContext(LinkContext);
  const isAbsolute = /^[a-z][a-z0-9+.-]*:/i.test(props.href) || props.href.startsWith('//');
  return isAbsolute ? PlainAnchor(props) : Provided(props);
}
